import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { mockData } from '../mock';
import Header from '../components/Header';
import Seo from '../components/Seo';
import ResponsiveImage from '../components/ResponsiveImage';
import { useCatalogueResource } from '../hooks/useCatalogueResource';

const ALL_CATEGORY = 'All';

const Blog = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORY);

  // Try backend; fall back to local mock posts
  const { data: posts } = useCatalogueResource('/api/blog', mockData.blogPosts);

  const categories = useMemo(() => {
    const unique = Array.from(new Set((posts || []).map((post) => post.category)));
    return [ALL_CATEGORY, ...unique];
  }, [posts]);

  const filteredPosts = useMemo(() => {
    if (selectedCategory === ALL_CATEGORY) return posts || [];
    return (posts || []).filter((post) => post.category === selectedCategory);
  }, [selectedCategory, posts]);

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="blog-page">
      <Seo
        title="Insights & Education"
        description="Evidence-based perspectives on strength, mobility, recovery, rehabilitation and longevity from the ZEEVA Life team."
        path="/blog"
      />
      <Header />

      <section className="bg-gradient-to-br from-[var(--bg-page)] to-[var(--bg-subtle)] py-16">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="section-title">Insights & Education</h1>
            <p className="section-subtitle mt-4">
              Evidence-based perspectives on strength, mobility, recovery,
              rehabilitation and longevity.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8 bg-[var(--bg-subtle)]">
        <div className="container">
          <div
            className="flex gap-3 justify-center flex-wrap"
            data-testid="blog-category-filter"
          >
            {categories.map((category) => (
              <Button
                key={`blog-category-${category}`}
                onClick={() => setSelectedCategory(category)}
                className={
                  selectedCategory === category ? 'btn-primary' : 'btn-secondary'
                }
                data-testid={`blog-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Card
                key={`blog-post-card-${post.id}`}
                className="blog-card cursor-pointer"
                onClick={() => navigate(`/blog/${post.slug}`)}
                data-testid={`blog-post-card-${post.id}`}
              >
                <div className="blog-card-image">
                  <ResponsiveImage
                    src={post.image}
                    alt={post.title}
                    sizes="(max-width: 768px) 100vw, 33vw"
                  />
                  <div className="blog-category-badge">{post.category}</div>
                </div>
                <CardHeader>
                  <CardTitle className="blog-card-title">{post.title}</CardTitle>
                  <div className="blog-meta">
                    <span className="blog-meta-item">
                      <Calendar size={16} aria-hidden="true" />
                      {new Date(post.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                    <span className="blog-meta-item">
                      <Clock size={16} aria-hidden="true" />
                      {post.readTime}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="blog-excerpt">
                    {post.excerpt}
                  </CardDescription>
                  <Button className="btn-primary mt-4 w-full">
                    Read More
                    <ArrowRight className="ml-2" size={18} aria-hidden="true" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-16">
              <p className="text-xl text-[var(--text-secondary)]">
                No posts found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="bg-[var(--bg-section)] py-16">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="section-title mb-4">
              Ready to Build Lifelong Capability?
            </h2>
            <p className="section-subtitle mb-8">
              Take the Zeeva Capability Assessment to map your baseline and find
              the right program.
            </p>
            <Button
              className="btn-cta"
              onClick={() => navigate('/assessment')}
              data-testid="blog-cta-assessment"
            >
              Start Your Assessment
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
